import type { Dependencies } from '../../types/Dependencies'
import type { PreparedImportRequest } from '@/shared/lib/documentImport'
import type {
  BatchCreateDocumentFieldsMutation,
  BatchCreateDocumentFieldsMutationVariables,
} from '@/graphql/mutations/batchCreateDocumentFieldsMutation.gql.generated'
import type { DocumentsLoadRequest, DocumentsStoreApi } from './documents-store'
import type { CollectionDoc } from '../../types/Doc'
import { BatchCreateDocumentFieldsDocument } from '@/graphql/mutations/batchCreateDocumentFieldsMutation.gql.generated'

type DocumentsSetState = DocumentsStoreApi['setState']

type NewImportColumn = Extract<
  PreparedImportRequest['columns'][number],
  { action: 'new' }
>

type PreparedImportDocument = Omit<CollectionDoc, '_id' | '_rev'> & {
  documentCollectionId: string
}

export async function importDocumentsAction({
  dependencies,
  loadDocuments,
  request,
  set,
}: {
  dependencies: Dependencies
  loadDocuments: (payload?: DocumentsLoadRequest) => Promise<void>
  request: PreparedImportRequest
  set: DocumentsSetState
}) {
  set({ importStatus: 'submitting', importError: null, lastImportResult: null })

  try {
    const result = await importDocuments({
      request,
      client: dependencies.client,
      records: dependencies.records,
    })

    set({
      importStatus: 'succeeded',
      importError: null,
      lastImportResult: result,
    })
    await loadDocuments({
      documentCollectionId: request.documentCollectionId,
      setLoadState: false,
    })
  } catch (error) {
    set({
      importStatus: 'failed',
      importError: getErrorMessage(error, 'Import failed.'),
    })
  }
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

async function importDocuments({
  request,
  client,
  records,
}: {
  request: PreparedImportRequest
  client: Dependencies['client']
  records: Dependencies['records']
}) {
  const newColumns = request.columns.filter(isNewImportColumn)
  const skippedColumnCount = request.columns.filter(
    (column) => column.action === 'skip',
  ).length
  const createdFieldMap = await createDocumentFields({
    client,
    newColumns,
    request,
  })
  const documents = buildImportDocuments({ createdFieldMap, request })

  await records.import(
    request.documentCollectionId,
    documents.map(
      ({ documentCollectionId: _documentCollectionId, ...values }) => values,
    ),
  )

  return {
    createdFieldCount: newColumns.length,
    importedRowCount: documents.length,
    skippedColumnCount,
  }
}

async function createDocumentFields({
  client,
  newColumns,
  request,
}: {
  client: Dependencies['client']
  newColumns: Array<NewImportColumn>
  request: PreparedImportRequest
}): Promise<Map<number, string>> {
  const createdFieldMap = new Map<number, string>()

  if (newColumns.length === 0) {
    return createdFieldMap
  }

  const mutationResult = await client.mutate<
    BatchCreateDocumentFieldsMutation,
    BatchCreateDocumentFieldsMutationVariables
  >({
    mutation: BatchCreateDocumentFieldsDocument,
    variables: {
      input: {
        documentCollectionId: request.documentCollectionId,
        fields: newColumns.map((column) => ({
          documentCollectionId: request.documentCollectionId,
          name: column.fieldName,
          kind: column.fieldKind,
        })),
      },
    },
  })

  const createdFields = mutationResult.data?.batchCreateDocumentFields

  if (!createdFields || createdFields.length !== newColumns.length) {
    throw new Error('Failed to create all document fields for this import.')
  }

  newColumns.forEach((column, index) => {
    createdFieldMap.set(column.index, createdFields[index].id)
  })

  return createdFieldMap
}

function buildImportDocuments({
  createdFieldMap,
  request,
}: {
  createdFieldMap: Map<number, string>
  request: PreparedImportRequest
}): Array<PreparedImportDocument> {
  return request.rows.map((row) =>
    buildImportDocument({
      columns: request.columns,
      createdFieldMap,
      documentCollectionId: request.documentCollectionId,
      row,
    }),
  )
}

function buildImportDocument({
  columns,
  createdFieldMap,
  documentCollectionId,
  row,
}: {
  columns: PreparedImportRequest['columns']
  createdFieldMap: Map<number, string>
  documentCollectionId: string
  row: Array<string>
}): PreparedImportDocument {
  const doc: PreparedImportDocument = {
    documentCollectionId,
  }
  for (const column of columns) {
    if (column.action === 'skip') {
      continue
    }

    const fieldId =
      column.action === 'existing'
        ? column.fieldId
        : createdFieldMap.get(column.index)

    if (!fieldId) {
      throw new Error(`Missing target field id for column "${column.header}".`)
    }

    doc[fieldId] = row[column.index] ?? ''
  }

  return doc
}

function isNewImportColumn(
  column: PreparedImportRequest['columns'][number],
): column is NewImportColumn {
  return column.action === 'new'
}
